import { useCallback, useMemo, useState } from 'react';
import { Input, Button, CodeCountdown } from '@/components';
import styled from 'styled-components';
import { Link, Flex, Container, BREAKPOINTS } from '@/ui';
import { userActions } from '@/store/user';
import { useDispatch, useSelector } from 'react-redux';
import { newObject } from '@/utils/helpers';
import { authApi, investorApi } from '@/api';
import { EMAIL_REGEX } from '@/utils/static';
import { Form } from 'antd';
import { Text, Title } from '@/ui/typography';
import { useTranslation } from 'react-i18next';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router';
import { usePopups } from '@/hooks';

export const LoginViaEmail = () => {
  const [codeSent, setCodeSent] = useState(false);
  const navigate = useNavigate();

  return (
    <StyledMainRow>
      <StyledContainer>
        <Button
          size='large'
          type='text'
          text='Back'
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
        />
      </StyledContainer>
      {codeSent ? <VerifyEmail /> : <InsertData goNext={() => setCodeSent(true)} />}
    </StyledMainRow>
  );
};

const InsertData = ({ goNext }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { showErrorToast, showInfoToast } = usePopups();
  const [data, setData] = useState({ email: '', password: '' });
  const [emailError, setEmailError] = useState('');
  const [isEmailFound, setIsEmailFound] = useState(null);
  const [emailValidated, setEmailValidated] = useState(false);

  const onChangeData = (key, value, data) => {
    setData(newObject(data, key, value));
  };

  const onChangeEmail = useCallback(
    (() => {
      let timer;

      return (...args) => {
        onChangeData(...args);

        if (timer) clearTimeout(timer);
        timer = setTimeout(() => {
          validateEmail(args[1]);
        }, 800);
      };
    })(),
    []
  );

  const checkValidation = () => {
    return new Promise((resolve, reject) => {
      if (data.email) {
        investorApi.phoneEmailValidation(null, data.email.toLowerCase(), null).then((res) => {
          if (res.status === 200) {
            const { data } = res;

            if (data.validEmail !== undefined) {
              setIsEmailFound(!data.validEmail);
              data.validEmail ? reject('UNIQUE_FAILED') : resolve();
            }
          }
        });
      } else {
        setIsEmailFound(null);
        reject();
      }
    });
  };

  const validateEmail = (email) => {
    const validEmail = EMAIL_REGEX.test(email.toLowerCase());

    !validEmail ? setEmailError('Invalid e-mail address') : setEmailError('');

    if (!emailValidated) setEmailValidated(true);
  };

  const handleNext = async () => {
    try {
      await checkValidation();

      dispatch(
        userActions.auth(
          {
            ...data,
            email: data.email.toLowerCase()
          },
          goNext
        )
      );
    } catch (e) {
      console.error(e);
      if (e === 'UNIQUE_FAILED') {
        showInfoToast("Account with this email doesn't exist");
      } else {
        showInfoToast(e, true);
      }
    }
  };

  const disableNext = useMemo(() => {
    return !emailValidated || !data.email || !data.password || emailError;
  }, [emailValidated, data.email, data.password, emailError]);

  return (
    <Container $maxWidth={500} style={{ width: '100%' }}>
      <Title level={2} $textAlign={'center'} $fontWeight={600}>
        {t('Log in to start your session')}
      </Title>

      <Form layout='vertical'>
        <StyledEmailFormContainer $marginTop={60}>
          <Input
            required
            label={t('E-mail')}
            value={data.email}
            status={isEmailFound === false ? 'error' : ''}
            onChange={(e) => onChangeEmail('email', e.target.value, data)}
            error={emailError}
            boldLabel
          />

          <Input
            marginTopLabel={24}
            required
            label={t('Password')}
            value={data.password}
            onChange={(e) => onChangeData('password', e.target.value, data)}
            password={true}
            boldLabel
          />

          <StyledReceiveRow align={'center'} justify={'space-between'} $marginTop={12} wrap>
            <Container>
              <Link to={'/reset-password'}>
                <Button type='text' size='large' text={`${t('Forgot password')}?`} />
              </Link>
            </Container>
          </StyledReceiveRow>
        </StyledEmailFormContainer>

        <Container $marginTop={28}>
          <Button
            type='filled'
            size='large'
            text={t('Log in')}
            onClick={handleNext}
            disabled={disableNext}
          />
        </Container>
      </Form>

      <StyledReceiveRow align={'center'} justify={'center'} $marginTop={40} gap={8} wrap>
        <Text $fontSize={18} type='secondary'>
          {t("Don't have an account yet?")}
        </Text>

        <Container>
          <Link to={'/registration/investor'}>
            <Button
              type='text'
              size={window.innerWidth <= BREAKPOINTS.sm ? 'medium' : 'large'}
              text={t('Sign up')}
            />
          </Link>
        </Container>
      </StyledReceiveRow>
    </Container>
  );
};

const VerifyEmail = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { email, id } = useSelector((state) => state.user);
  const [code, setCode] = useState('');

  const handleNext = () => {
    dispatch(userActions.setTokenByEmail({ email: email, code: code, id: id }, t));
  };

  return (
    <Wrapper>
      <Title level={2}>{t('Verify your account')}</Title>

      <Container $marginTop={20}>
        <Text $fontSize={16}>{t('The verification code has been sent on your e-mail:')}</Text>
      </Container>

      <Form layout='vertical'>
        <StyledVerificationContainer>
          <Input
            label={t('Verification code')}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            type={'number'}
            boldLabel
          />
        </StyledVerificationContainer>

        <StyledReceiveRow $marginTop={54} $marginBottom={24} $gap={8} $alignCenter>
          <CodeCountdown sendCode={() => authApi.resendEmailCode(email, id)} />
        </StyledReceiveRow>

        <Button type='filled' size='large' text={t('Next')} onClick={handleNext} />
      </Form>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  width: 500px;
  @media (max-width: ${BREAKPOINTS.smd}px) {
    width: 100%;
  }
`;

const StyledContainer = styled(Container)`
  position: absolute;
  top: 30px;
  left: 60px;
  width: min-content;
  z-index: 129;
  @media (max-width: ${BREAKPOINTS.md}px) {
    top: 15px;
    left: 15px;
  }
`;

const StyledMainRow = styled(Flex)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-height: 100dvh;
  padding: 110px 16px;

  @media (max-width: ${BREAKPOINTS.smd}px) {
    padding: 80px 24px;
  }
`;

const StyledEmailFormContainer = styled(Container)`
  @media (max-width: ${BREAKPOINTS.smd}px) {
    margin-top: 24px;
  }
`;

const StyledVerificationContainer = styled(Container)`
  margin-top: 48px;

  @media (max-width: ${BREAKPOINTS.smd}px) {
    margin: 36px 0;
  }
`;

const StyledReceiveRow = styled(Flex)`
  @media (max-width: ${BREAKPOINTS.smd}px) {
    margin: 24px 0;
  }
`;
